<template>
  <Transition name="modal">
    <div v-if="isOpen" class="modal-overlay" @click="close">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">快捷键指南</h2>
          <button class="close-button" @click="close">×</button>
        </div>

        <div class="modal-body">
          <!-- 搜索框 -->
          <div class="search-box">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索快捷键..."
              class="search-input"
            />
          </div>

          <!-- 快捷键列表 -->
          <div class="shortcuts-list">
            <div
              v-for="category in filteredCategories"
              :key="category.name"
              class="shortcut-category"
            >
              <h3 class="category-title">{{ category.name }}</h3>
              <div class="category-items">
                <div
                  v-for="item in category.items"
                  :key="item.key"
                  class="shortcut-item"
                >
                  <div class="shortcut-keys">
                    <kbd
                      v-for="(key, index) in item.keys"
                      :key="index"
                      class="key"
                    >
                      {{ key }}
                    </kbd>
                    <span v-if="index < item.keys.length - 1" class="key-separator">+</span>
                  </div>
                  <div class="shortcut-description">
                    <span class="action-name">{{ item.action }}</span>
                    <span v-if="item.description" class="action-desc">
                      {{ item.description }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 无结果 -->
          <div v-if="filteredCategories.length === 0" class="no-results">
            <p>未找到匹配的快捷键</p>
          </div>
        </div>

        <div class="modal-footer">
          <p class="footer-hint">
            提示：按 <kbd>?</kbd> 或 <kbd>/</kbd> 快速打开此面板
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  'close': [];
}>();

const searchQuery = ref('');

const shortcutCategories = [
  {
    name: '创建与编辑',
    items: [
      { keys: ['Ctrl', 'N'], action: '新建项目', description: '聚焦到快速添加输入框' },
      { keys: ['Ctrl', 'Enter'], action: '保存', description: '在编辑模式下保存更改' },
      { keys: ['Escape'], action: '取消编辑', description: '退出编辑模式' },
    ],
  },
  {
    name: '操作',
    items: [
      { keys: ['Ctrl', 'Z'], action: '撤销', description: '撤销上一次操作' },
      { keys: ['Ctrl', 'Shift', 'Z'], action: '重做', description: '重做撤销的操作' },
      { keys: ['Ctrl', 'Y'], action: '重做', description: '重做撤销的操作' },
    ],
  },
  {
    name: '导航',
    items: [
      { keys: ['?'], action: '打开快捷键帮助', description: '显示此面板' },
      { keys: ['/'], action: '打开快捷键帮助', description: '显示此面板' },
      { keys: ['Escape'], action: '关闭面板', description: '关闭当前打开的面板' },
    ],
  },
  {
    name: '其他',
    items: [
      { keys: ['拖拽'], action: '快速添加', description: '拖拽文本/链接/图片到页面' },
    ],
  },
];

const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) {
    return shortcutCategories;
  }

  const query = searchQuery.value.toLowerCase();
  return shortcutCategories
    .map(category => ({
      ...category,
      items: category.items.filter(item => {
        const actionMatch = item.action.toLowerCase().includes(query);
        const descMatch = item.description?.toLowerCase().includes(query);
        const keysMatch = item.keys.some(key => key.toLowerCase().includes(query));
        return actionMatch || descMatch || keysMatch;
      }),
    }))
    .filter(category => category.items.length > 0);
});

function close() {
  emit('close');
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.3s ease-out;
}

@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-button {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 50%;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}

.close-button:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.search-box {
  margin-bottom: 1.5rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.shortcuts-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.shortcut-category {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-primary);
}

.category-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  transition: all 0.2s;
}

.shortcut-item:hover {
  background: var(--bg-tertiary);
  transform: translateX(4px);
}

.shortcut-keys {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  min-width: 150px;
}

.key {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: var(--bg-secondary);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.key-separator {
  color: var(--text-tertiary);
  font-size: 0.75rem;
  margin: 0 0.25rem;
}

.shortcut-description {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.action-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.action-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: var(--text-tertiary);
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.footer-hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-align: center;
}

.footer-hint kbd {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  background: var(--bg-secondary);
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.75rem;
  margin: 0 0.125rem;
}

/* 过渡动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(-20px);
}

/* 暗夜模式 */
:global(.dark) .modal-content {
  background: var(--bg-secondary) !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3) !important;
}

:global(.dark) .modal-header {
  border-bottom-color: var(--text-secondary) !important;
}

:global(.dark) .modal-title {
  color: var(--text-primary) !important;
}

:global(.dark) .close-button {
  background: var(--bg-tertiary) !important;
  color: var(--text-secondary) !important;
}

:global(.dark) .close-button:hover {
  background: var(--border-color) !important;
  color: var(--text-primary) !important;
}

:global(.dark) .search-input {
  background: var(--bg-tertiary) !important;
  border-color: var(--border-color) !important;
  color: var(--text-primary) !important;
}

:global(.dark) .search-input:focus {
  border-color: var(--color-primary-light) !important;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.1) !important;
}

:global(.dark) .category-title {
  color: var(--text-secondary) !important;
}

:global(.dark) .shortcut-item {
  background: var(--bg-tertiary) !important;
}

:global(.dark) .shortcut-item:hover {
  background: var(--border-color) !important;
}

:global(.dark) .key {
  background: var(--bg-secondary) !important;
  border-color: var(--border-color) !important;
  color: var(--text-secondary) !important;
}

:global(.dark) .action-name {
  color: var(--text-primary) !important;
}

:global(.dark) .modal-footer {
  border-top-color: var(--text-secondary) !important;
  background: var(--bg-primary) !important;
}

@media (max-width: 640px) {
  .modal-content {
    margin: 0;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .shortcut-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .shortcut-keys {
    min-width: auto;
  }
}
</style>
